import { type ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2, Pin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { formatDateTime } from '@/lib/date';
import { AnnouncementStatus, type AnnouncementListItemDto } from '../types';
import { PriorityBadge } from './PriorityBadge';

const STATUS_LABEL: Record<AnnouncementStatus, string> = {
  [AnnouncementStatus.Draft]: 'Draft',
  [AnnouncementStatus.Published]: 'Published',
  [AnnouncementStatus.Archived]: 'Archived',
};

const STATUS_CLS: Record<AnnouncementStatus, string> = {
  [AnnouncementStatus.Draft]: 'bg-muted text-muted-foreground',
  [AnnouncementStatus.Published]: 'bg-success/10 text-success',
  [AnnouncementStatus.Archived]: 'bg-muted text-muted-foreground',
};

interface ColumnFactoryArgs {
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

export function createAnnouncementColumns({
  onEdit,
  onDelete,
}: ColumnFactoryArgs): ColumnDef<AnnouncementListItemDto>[] {
  return [
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          {row.original.isPinned && (
            <Pin className="h-3.5 w-3.5 text-capitol-red-500" aria-label="Pinned" />
          )}
          <span className="font-medium">{row.original.title}</span>
        </div>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[row.original.status]}`}
        >
          {STATUS_LABEL[row.original.status]}
        </span>
      ),
    },
    {
      accessorKey: 'publishAt',
      header: 'Publish at',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.publishAt ? formatDateTime(row.original.publishAt) : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'expiresAt',
      header: 'Expires',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.expiresAt ? formatDateTime(row.original.expiresAt) : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <PermissionGuard permission={PERMISSIONS.ANNOUNCEMENT_EDIT}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(row.original.id)}
              aria-label="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.ANNOUNCEMENT_DELETE}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onDelete(row.original.id)}
              aria-label="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </PermissionGuard>
        </div>
      ),
    },
  ];
}
